import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ServicesCardsPrivateCloud.css';
import { TimelineFormation } from '../../ui/timeline-formation';
import FeatureDetailModal from './FeatureDetailModal';
import './timeline-custom.css';

// Import icons directly
import ProcessMobile1 from '../../../assets/images/itm01.png';
import ProcessMobile2 from '../../../assets/images/itm02.png';
import ProcessMobile3 from '../../../assets/images/itm03.png';
import ProcessMobile4 from '../../../assets/images/itm04.png';
// Assuming icons for 5, 6, 7 are the same as 4 based on previous data structure
// import ProcessMobile5 from '../../../assets/images/itm05.png'; // If different icons exist

interface DetailModalState {
  isOpen: boolean;
  title: string;
  content: string;
}

// Define necessary types locally based on usage
interface TimelineItemFeature {
  id: string;
  text: string; // Ensure text property exists
  detailContent?: string;
  element?: React.ReactNode; // For the "Learn More" button - intentionally kept for Cloud Fundamentals
}

interface TimelineItem {
  date: string;
  title: string;
  description: string;
  icon: string;
  features: TimelineItemFeature[];
}

// Timeline specifically for Cloud Fundamentals with 7 modules and Learn More buttons
const TimelineFormationCF: React.FC = () => {
  const { t } = useTranslation();
  const [detailModal, setDetailModal] = useState<DetailModalState>({
    isOpen: false,
    title: '',
    content: '',
  });

  // Use the locally defined feature type
  const openDetailModal = (feature: TimelineItemFeature) => {
    if (feature.detailContent) {
      setDetailModal({
        isOpen: true,
        title: feature.text, // Now uses the correct type with 'text'
        content: feature.detailContent,
      });
    }
  };

  const closeDetailModal = () => {
    setDetailModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Cloud Fundamentals has 7 modules
  const serviceIds = [1, 2, 3, 4, 5, 6, 7];

  const icons: { [key: number]: string } = {
    1: ProcessMobile1,
    2: ProcessMobile2,
    3: ProcessMobile3,
    4: ProcessMobile4,
    5: ProcessMobile4,
    6: ProcessMobile4,
    7: ProcessMobile4,
  };
  const getIconForService = (id: number) => icons[id] || ProcessMobile1;

  // Explicitly type timelineItems with the locally defined TimelineItem[]
  const timelineItems: TimelineItem[] = serviceIds.map(id => {
    const serviceKey = `formationCF.timeline.services.${id}`;
    const featuresTranslationResult = t(`${serviceKey}.features`, { returnObjects: true, defaultValue: {} });

    const featuresData = typeof featuresTranslationResult === 'object' && featuresTranslationResult !== null 
      ? featuresTranslationResult as Record<string, { text?: string; detailContent?: string }> 
      : {};

    const featureKeys = Object.keys(featuresData);

    return {
      date: "",
      title: t(`${serviceKey}.title`),
      description: t(`${serviceKey}.description`),
      icon: getIconForService(id),
      // Map to the locally defined TimelineItemFeature type
      features: featureKeys
        .map(featureId => {
          const featureTextKey = `${serviceKey}.features.${featureId}.text`;
          const featureDetailKey = `${serviceKey}.features.${featureId}.detailContent`;
          const translatedText = t(featureTextKey);
          const translatedDetail = t(featureDetailKey, { defaultValue: undefined });

          // Define the feature object matching TimelineItemFeature
          const feature: TimelineItemFeature = {
            id: featureId,
            text: translatedText,
            detailContent: translatedDetail || undefined,
          };

          // Add the clickable element directly if detail content exists
          if (feature.detailContent) {
            feature.element = (
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); // Prevent potential parent handlers
                  openDetailModal(feature); 
                }}
                className="tw-ml-2 tw-text-blue-600 hover:tw-underline tw-text-sm focus:tw-outline-none">
                ({t('common.learnMore')})
              </button>
            );
          }

          return feature;
        })
        // Filter out features where text is missing or equals the key (untranslated)
        .filter(f => f.text && f.text !== `${serviceKey}.features.${f.id}.text`), 
    };
  });

  useEffect(() => {
    const timelineCardTitles = document.querySelectorAll('.slider-cards h3.blue-h2-blue-bg');
    timelineCardTitles.forEach(element => {
      if (element instanceof HTMLElement) {
        element.style.paddingLeft = '30px';
      }
    });
  }, []);

  return (
    <div className="tw-container entra-timeline tw-mx-auto tw-px-4 tw-pb-16 tw-bg-transparent">
      <div className="centered-title-dark-bg">
        <h2>{t('formationCF.timeline.title')}</h2>
      </div>
      {/* Pass items typed as TimelineItem[] */}
      <TimelineFormation items={timelineItems} showModule={true} moduleText="Module" />

      <FeatureDetailModal
        isOpen={detailModal.isOpen}
        onClose={closeDetailModal}
        title={detailModal.title}
        content={detailModal.content}
      />
    </div>
  );
};

export default TimelineFormationCF;